/**
 * unilar Header JavaScript - Based on Figma Designs
 * Handles announcement carousel, search, CEP modal, account dropdown,
 * megamenu, mobile menu, cart, and countdown timer functionality
 */

function interpolateHeaderI18n(template, vars) {
  if (!template) return '';
  return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, key) {
      return vars[key] != null ? String(vars[key]) : '';
  });
}

function escapeHtml(text) {
  if (text == null) return '';
  return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
}

function parseHeaderunilarI18n() {
  const defaults = {
      deliver_prefix: 'Deliver to',
      search_loading: 'Searching…',
      search_empty_message: 'See full results for "{{ query }}".',
      search_view_all: 'See all results',
      search_view_all_for: 'See all results for "{{ query }}"',
      search_error: 'Could not load products. Please try again.',
      search_more_colors: '+ {{ count }} colors',
      cep_invalid_alert: 'Invalid postal code. Enter an 8-digit postal code.',
      loading: 'Loading…',
      cep_not_found_alert: 'Postal code not found. Check the number and try again.',
      enter_cep: 'Enter your postal code',
      geo_unavailable: 'Geolocation is not available in your browser.',
      geo_fetching: 'Getting location…',
      geo_reverse_failed: 'Could not get a postal code from your location. Please enter it manually.',
      geo_permission_denied: 'Permission denied. Enable location in your browser settings.',
      geo_position_unavailable: 'Location is unavailable right now.',
      geo_timeout: 'Timed out while getting location.',
      geo_generic_error: 'Could not get your location.',
      price_for_label: 'for:',
      pix_price_line: 'Pay in full with PIX ({{ discount }}% OFF)'
  };
  const el = document.getElementById('header-unilar-i18n');
  if (!el || !String(el.textContent || '').trim()) {
      return defaults;
  }
  try {
      const parsed = JSON.parse(el.textContent);
      const out = { ...defaults };
      for (const key of Object.keys(parsed)) {
          if (parsed[key] != null && parsed[key] !== '') {
              out[key] = parsed[key];
          }
      }
      return out;
  } catch (error) {
      return defaults;
  }
}

class unilarHeader {
  constructor() {
      this.str = parseHeaderunilarI18n();
      this.init();
  }

  h(key, vars) {
      return interpolateHeaderI18n(this.str[key], vars);
  }

  pixPriceLine(discount) {
      return this.h('pix_price_line', { discount: discount });
  }

  init() {
      // Initialize all components
      this.initSearch();
      this.initCEPModal();
      this.initAccountDropdown();
      this.initMegamenu();
      this.initMobileMenu();
      this.initCountdown();
      this.initMinicart();
      this.initStickyHeader();

      // Check for CEP parameter in URL
      this.checkURLForCEP();

      // Listen for CEP updates from other components (e.g., shipping-rates.js, unilar-cart-shipping.js)
      document.addEventListener('cep:updated', (event) => {
          this.updateHeaderFromEvent(event.detail);
      });
  }

  /**
   * Sticky header: follows on scroll (desktop + mobile).
   * On mobile, collapses search/CEP rows until the page returns to the top.
   */
  initStickyHeader() {
      this.headerEl = document.querySelector('[data-header-unilar]');
      if (!this.headerEl) return;

      this.collapseScrollThreshold = 16;
      this.expandScrollThreshold = 1;
      this.isScrolled = false;
      this.stickyScrollRaf = null;

      this.handleStickyScroll = () => {
          if (this.stickyScrollRaf !== null) return;

          this.stickyScrollRaf = requestAnimationFrame(() => {
              this.stickyScrollRaf = null;
              const scrollTop = window.scrollY;
              let scrolled = this.isScrolled;

              // Different limits prevent the changing header height from
              // repeatedly opening and closing near the top of the page.
              if (!this.isScrolled && scrollTop > this.collapseScrollThreshold) {
                  scrolled = true;
              } else if (this.isScrolled && scrollTop <= this.expandScrollThreshold) {
                  scrolled = false;
              }

              if (scrolled === this.isScrolled) return;

              this.isScrolled = scrolled;
              // Disable scroll anchoring while the sticky header changes
              // height; otherwise Chromium can adjust scrollY and retrigger
              // the opposite state during the transition.
              document.documentElement.style.overflowAnchor = 'none';
              this.headerEl.classList.toggle('is-scrolled', scrolled);
              this.updateHeaderHeightVar();

              // Re-measure and restore anchoring after the transition.
              clearTimeout(this.headerHeightTimeout);
              this.headerHeightTimeout = setTimeout(() => {
                  this.updateHeaderHeightVar();
                  document.documentElement.style.overflowAnchor = '';
              }, 320);
          });
      };

      this.stickyTopOffset = 0;
      this.initStickyTopBanners();
      this.updateHeaderHeightVar();
      this.handleStickyScroll();

      window.addEventListener('scroll', this.handleStickyScroll, { passive: true });
      window.addEventListener('resize', () => this.updateHeaderHeightVar(), { passive: true });
  }

  /**
   * Deco (Labels & Badges) inserts its top banners as direct children of
   * <body>; with "sticky" enabled they get position: sticky; top: 0;
   * z-index: 9999 and cover the top of the header (cutting the logo).
   * Track their height so the header sticks right below them.
   */
  initStickyTopBanners() {
      const selector = 'body > .deco-carousel-container';
      const resizeObserver = new ResizeObserver(() => this.updateStickyTopOffset());

      const observeBanners = () => {
          resizeObserver.disconnect();
          this.stickyTopBanners = Array.from(document.querySelectorAll(selector));
          this.stickyTopBanners.forEach((banner) => resizeObserver.observe(banner));
          this.updateStickyTopOffset();
      };

      // Banners are injected asynchronously and removed when closed/expired.
      new MutationObserver(observeBanners).observe(document.body, { childList: true });
      observeBanners();
  }

  updateStickyTopOffset() {
      // Sticky banners all pin at top: 0 and overlap each other, so the
      // covered area is the tallest one, not the sum.
      const offset = (this.stickyTopBanners || []).reduce((max, banner) => {
          if (getComputedStyle(banner).position !== 'sticky') return max;
          return Math.max(max, banner.offsetHeight);
      }, 0);

      if (offset === this.stickyTopOffset) return;

      this.stickyTopOffset = offset;
      document.body.style.setProperty('--unilar-sticky-top-offset', `${offset}px`);
      this.updateHeaderHeightVar();
  }

  updateHeaderHeightVar() {
      if (!this.headerEl) return;
      // Includes the sticky top banner so elements pinned below the header
      // (top: var(--header-height)) are not covered by it.
      const height = this.headerEl.offsetHeight + (this.stickyTopOffset || 0);
      document.body.style.setProperty('--header-height', `${height}px`);
  }

  /**
   * Check URL for CEP parameter and update if present
   */
  checkURLForCEP() {
      const urlParams = new URLSearchParams(window.location.search);
      const cepParam = urlParams.get('cep') || urlParams.get('CEP');

      if (cepParam) {
          // Clean the CEP parameter (remove any non-numeric characters)
          const cleanCEP = cepParam.replace(/\D/g, '');

          // Validate CEP length
          if (cleanCEP.length === 8) {
              // Update CEP automatically
              this.updateCEP(cleanCEP);
          } else {
              console.warn(`CEP inválido na URL: ${cepParam}. O CEP deve ter 8 dígitos.`);
          }
      }
  }

  updateHeaderFromEvent(addressData) {
      // Update desktop CEP display: "Entregar em Cidade, UF - CEP"
      const desktopAddressTexts = document.querySelectorAll('.unilar-address-text');
      desktopAddressTexts.forEach(container => {
          // Verifica se já existe um label, se não, cria
          let label = container.querySelector('.unilar-address-label');
          if (!label) {
              label = document.createElement('span');
              label.className = 'unilar-address-label';
              label.textContent = this.str.deliver_prefix;
              container.insertBefore(label, container.firstChild);
          }

          // Atualiza o valor
          const valueEl = container.querySelector('.unilar-address-value');
          if (valueEl) {
              valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
          }
      });

      const mobileCepText = document.querySelector('.unilar-mobile-cep-text');
      if (mobileCepText) {
          
          let label = mobileCepText.querySelector('.unilar-address-label');
          if (!label) {
              label = document.createElement('span');
              label.className = 'unilar-address-label';
              label.textContent = this.str.deliver_prefix;
              mobileCepText.insertBefore(label, mobileCepText.firstChild);
          }

          let valueEl = mobileCepText.querySelector('.unilar-cep-default');
          if (!valueEl) {
              valueEl = document.createElement('span');
              valueEl.className = 'unilar-cep-default';
              mobileCepText.appendChild(valueEl);
          }
          valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
      }
  }

  buildProductSearchUrl(query) {
      const base =
          typeof Theme !== 'undefined' && Theme.routes && Theme.routes.search_url
              ? Theme.routes.search_url
              : '/search';
      const params = new URLSearchParams();
      params.set('q', query);
      params.set('type', 'product');
      params.set('options[prefix]', 'last');
      return `${base}?${params.toString()}`;
  }

  // ========== Search Modal ==========
  initSearch() {
      const searchTriggers = document.querySelectorAll('[data-search-trigger]');
      const searchField = document.querySelector('[data-search-field]');
      const searchModal = document.querySelector('[data-search-modal]');
      const searchClose = document.querySelector('[data-search-close]');
      const searchInput = document.querySelector('[data-search-input]');
      const searchOverlay = document.querySelector('[data-search-overlay]');
      const searchResults = document.querySelector('[data-search-results]');
      const searchDefault = document.querySelector('[data-search-default]');

      if (!searchTriggers.length) return;

      let searchTimeout = null;

      const openSearchModal = () => {
          this.openModal(searchModal);
          if (searchInput) {
              setTimeout(() => searchInput.focus(), 100);
          }
      };

      searchTriggers.forEach(trigger => {
          trigger.addEventListener('click', openSearchModal);
      });

      document.addEventListener('keydown', (event) => {
          if (!event.metaKey || event.key !== 'k' || !searchModal) return;

          event.preventDefault();

          if (!searchModal.classList.contains('unilar-hidden')) {
              searchInput?.focus();
              return;
          }

          openSearchModal();
      });

      // Make search field button clickable to focus input
      if (searchField && searchInput) {
          searchField.addEventListener('click', () => {
              searchInput.focus();
          });
      }

      if (searchClose) {
          searchClose.addEventListener('click', () => {
              this.closeModal(searchModal);
              this.clearSearch(searchInput, searchResults, searchDefault);
          });
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && searchModal && !searchModal.classList.contains('unilar-hidden')) {
              this.closeModal(searchModal);
              this.clearSearch(searchInput, searchResults, searchDefault);
          }
      });

      // Close on backdrop/overlay click
      if (searchOverlay) {
          searchOverlay.addEventListener('click', () => {
              this.closeModal(searchModal);
              this.clearSearch(searchInput, searchResults, searchDefault);
          });
      }

      // Predictive search on input
      if (searchInput) {
          searchInput.addEventListener('input', (e) => {
              const query = e.target.value.trim();

              // Clear previous timeout
              if (searchTimeout) {
                  clearTimeout(searchTimeout);
              }

              // Show default content if query is empty
              if (query.length === 0) {
                  if (searchResults) searchResults.classList.add('unilar-hidden');
                  if (searchDefault) searchDefault.classList.remove('unilar-hidden');
                  return;
              }

              // Wait 300ms before searching
              searchTimeout = setTimeout(() => {
                  this.performSearch(query, searchResults, searchDefault);
              }, 300);
          });

          // Handle enter key to go to search results page
          searchInput.addEventListener('keydown', (e) => {
              if (e.key === 'Enter') {
                  e.preventDefault();
                  const query = searchInput.value.trim();
                  if (query) {
                      window.location.href = this.buildProductSearchUrl(query);
                  }
              }
          });
      }
  }

  /**
   * @param {any} product
   * @returns {string}
   */
  buildFallbackSearchProductCard(product) {
      const hasDiscount = product.compare_at_price_max && product.compare_at_price_max > product.price;
      const variantCount = product.variants?.length || 1;
      const imageUrl = product.featured_image?.url;

      return `
          <a href="${product.url}" class="unilar-search-product-card">
              <div class="unilar-search-product-image">
              ${imageUrl ? `
                  <img src="${imageUrl}" alt="${escapeHtml(product.title)}" loading="lazy">
              ` : `
                  <div class="unilar-search-product-placeholder">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                      <rect width="48" height="48" rx="4" fill="#f5f5f5"/>
                      <path d="M24 14v20M14 24h20" stroke="#d0d0d0" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                  </div>
              `}

              ${variantCount > 1 ? `
                  <div class="unilar-color-badge unilar-color-badge--search">
                  ${escapeHtml(interpolateHeaderI18n(this.str.search_more_colors, { count: variantCount - 1 }))}
                  </div>
              ` : ''}
              </div>

              <div class="unilar-search-product-info">
              <h4 class="unilar-search-product-title">${escapeHtml(product.title)}</h4>

              <div class="unilar-search-product-pricing">
                  ${hasDiscount ? `
                  <div class="unilar-search-product-price-compare">${this.formatMoney(product.compare_at_price_max)}</div>
                  ` : ''}
                  <div class="unilar-search-product-price-row">
                  <span class="unilar-search-product-price-label">${escapeHtml(this.str.price_for_label)}</span>
                  <span class="unilar-search-product-price-current">${this.formatMoney(Math.ceil(product.price * 0.9))}</span>
                  </div>
                  <div class="unilar-search-product-price-installment">
                  ${escapeHtml(this.pixPriceLine(10))}
                  </div>
              </div>
              </div>
          </a>
      `;
  }

  /**
   * @param {any} product
   * @param {number} requestId
   * @returns {Promise<string | null>}
   */
  fetchSearchProductCard(product, requestId) {
      const sectionId = 'unilar-header-search-product-card';
      if (this._searchCardSectionAvailable === false) {
          return Promise.resolve(this.buildFallbackSearchProductCard(product));
      }

      return fetch(`/products/${encodeURIComponent(product.handle)}?section_id=${sectionId}`)
          .then((response) => {
              if (!response.ok) {
                  this._searchCardSectionAvailable = false;
                  return this.buildFallbackSearchProductCard(product);
              }
              this._searchCardSectionAvailable = true;
              return response.text();
          })
          .then((html) => {
              if (requestId !== this._searchRequestId) return null;
              if (!html || html.includes('unilar-search-product-card')) return html;
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const card = doc.querySelector('product-card.unilar-main-product-card');
              return card ? card.outerHTML : this.buildFallbackSearchProductCard(product);
          })
          .catch(() => this.buildFallbackSearchProductCard(product));
  }

  /**
   * @param {string} query
   * @param {any} resultsContainer
   * @param {any} defaultContainer
   */
  performSearch(query, resultsContainer, defaultContainer) {
      if (!resultsContainer) return;

      this._searchRequestId = (this._searchRequestId || 0) + 1;
      const requestId = this._searchRequestId;

      // Show loading state
      resultsContainer.innerHTML = '<div class="unilar-search-loading" style="padding: 40px 24px;">' + escapeHtml(this.str.search_loading) + '</div>';
      resultsContainer.classList.remove('unilar-hidden');
      if (defaultContainer) defaultContainer.classList.add('unilar-hidden');

      // Use Shopify's predictive search API (fields include variants.sku — not in API default)
      fetch(
          `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=10&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku,tag`
      )
          .then(response => response.json())
          .then(async (data) => {
              if (requestId !== this._searchRequestId) return;

              let products = /** @type {any[]} */ (data.resources.results.products || []);
              const rank = globalThis.unilarSearchRelevance?.sortProductsByQuery;
              if (typeof rank === 'function' && query.trim()) {
                  products = rank(products, query);
              }

              if (products.length === 0) {
                  resultsContainer.innerHTML = `
                      <div class="unilar-search-empty" style="padding: 40px 24px;">
                      <p>${this.h('search_empty_message', { query: escapeHtml(query) })}</p>
                      <a href="${this.buildProductSearchUrl(query)}" class="unilar-search-view-all-btn">
                          ${escapeHtml(this.str.search_view_all)}
                      </a>
                      </div>
                  `;
                  return;
              }

              const cards = await Promise.all(
                  products.map((product) => this.fetchSearchProductCard(product, requestId))
              );

              if (requestId !== this._searchRequestId) return;

              const cardMarkup = cards.filter(Boolean).join('');

              resultsContainer.innerHTML = `
              <div class="unilar-search-products-grid">
                  ${cardMarkup}
              </div>
              <div style="padding: 0; margin-top: 16px;">
                  <a href="${this.buildProductSearchUrl(query)}" class="unilar-search-view-all-btn">
                  ${this.h('search_view_all_for', { query: escapeHtml(query) })}
                  </a>
              </div>
              `;
          })
          .catch(error => {
              if (requestId !== this._searchRequestId) return;
              console.error('Search error:', error);
              resultsContainer.innerHTML = `
                  <div class="unilar-search-error" style="padding: 40px 24px;">
                      <p>${escapeHtml(this.str.search_error)}</p>
                  </div>`;
          });
  }

  clearSearch(input, results, defaultContainer) {
      if (input) input.value = '';
      if (results) results.classList.add('unilar-hidden');
      if (defaultContainer) defaultContainer.classList.remove('unilar-hidden');
  }

  formatMoney(cents) {
      return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
      }).format(cents);
  }

  // ========== CEP/Address Modal ==========
  initCEPModal() {
      const cepTriggers = document.querySelectorAll('[data-cep-trigger]');
      const cepModal = document.querySelector('[data-cep-modal]');
      const cepClose = document.querySelector('[data-cep-close]');
      const cepOverlay = document.querySelector('[data-cep-overlay]');
      const cepForm = document.querySelector('[data-cep-form]');
      const cepInput = document.querySelector('[data-cep-input]');
      const useLocationBtn = document.querySelector('[data-use-location]');

      if (!cepTriggers.length) return;

      cepTriggers.forEach(trigger => {
          trigger.addEventListener('click', () => {
              this.openModal(cepModal);
              if (cepInput) {
                  setTimeout(() => cepInput.focus(), 100);
              }
          });
      });

      if (cepClose) {
          cepClose.addEventListener('click', () => {
              this.closeModal(cepModal);
          });
      }

      if (cepOverlay) {
          cepOverlay.addEventListener('click', () => {
              this.closeModal(cepModal);
          });
      }

      if (cepForm) {
          cepForm.addEventListener('submit', (e) => {
              e.preventDefault();
              const cepValue = cepInput?.value.trim();
              if (cepValue) {
                  this.updateCEP(cepValue);
                  this.closeModal(cepModal);
              }
          });
      }

      // Format CEP as user types
      if (cepInput) {
          cepInput.addEventListener('input', (e) => {
              let value = e.target.value.replace(/\D/g, '');
              if (value.length > 5) {
                  value = value.slice(0, 5) + '-' + value.slice(5, 8);
              }
              e.target.value = value;
          });
      }

      // Use geolocation
      if (useLocationBtn) {
          useLocationBtn.addEventListener('click', () => {
              this.useGeolocation(cepModal);
          });
      }

      // Close on escape key
      document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && cepModal && !cepModal.classList.contains('unilar-hidden')) {
              this.closeModal(cepModal);
          }
      });

      // Load saved address on page load
      this.loadSavedAddress();
  }

  loadSavedAddress() {
      const savedAddress = localStorage.getItem('unilar-address');

      if (savedAddress) {
          try {
              const addressData = JSON.parse(savedAddress);

              // Update desktop CEP display
              const desktopAddressTexts = document.querySelectorAll('.unilar-address-text');
              desktopAddressTexts.forEach(container => {
                  // Verifica se já existe um label, se não, cria
                  let label = container.querySelector('.unilar-address-label');
                  if (!label) {
                      label = document.createElement('span');
                      label.className = 'unilar-address-label';
                      label.textContent = this.str.deliver_prefix;
                      container.insertBefore(label, container.firstChild);
                  }

                  // Atualiza o valor
                  const valueEl = container.querySelector('.unilar-address-value');
                  if (valueEl) {
                      valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
                  }
              });

              // Update mobile CEP display
              const mobileCepText = document.querySelector('.unilar-mobile-cep-text');
              if (mobileCepText) {
                  // Verifica se já existe um label, se não, cria
                  let label = mobileCepText.querySelector('.unilar-address-label');
                  if (!label) {
                      label = document.createElement('span');
                      label.className = 'unilar-address-label';
                      label.textContent = this.str.deliver_prefix;
                      mobileCepText.insertBefore(label, mobileCepText.firstChild);
                  }

                  // Atualiza ou cria o elemento de valor
                  let valueEl = mobileCepText.querySelector('.unilar-cep-default');
                  if (!valueEl) {
                      valueEl = document.createElement('span');
                      valueEl.className = 'unilar-cep-default';
                      mobileCepText.appendChild(valueEl);
                  }
                  valueEl.textContent = `${addressData.city}, ${addressData.state} - ${addressData.cep}`;
              }

              // Trigger event for other components
              try {
                  sessionStorage.setItem('unilar-address', JSON.stringify({
                      ...addressData,
                      updatedAt: addressData.updatedAt || Date.now()
                  }));
              } catch (storageError) {
                  console.warn('Erro ao sincronizar CEP no sessionStorage:', storageError);
              }
              document.dispatchEvent(new CustomEvent('cep:updated', {
                  detail: addressData
              }));
          } catch (error) {
              console.error('Erro ao carregar endereço salvo:', error);
          }
      }
  }

  updateCEP(cep) {
      // Remove any non-numeric characters from CEP
      const cleanCEP = cep.replace(/\D/g, '');

      if (cleanCEP.length !== 8) {
          alert(this.str.cep_invalid_alert);
          return;
      }

      // Show loading state
      const desktopAddressTexts = document.querySelectorAll('.unilar-address-text');
      const mobileCepTextContainer = document.querySelector('.unilar-mobile-cep-text');

      desktopAddressTexts.forEach(container => {
          const valueEl = container.querySelector('.unilar-address-value');
          if (valueEl) {
              valueEl.textContent = this.str.loading;
          }
      });

      if (mobileCepTextContainer) {
          const mobileCepValue = mobileCepTextContainer.querySelector('.unilar-cep-default');
          if (mobileCepValue) {
              mobileCepValue.textContent = this.str.loading;
          }
      }

      // Call Brasil API
      fetch(`https://brasilapi.com.br/api/cep/v1/${cleanCEP}`)
          .then(response => response.json())
          .then(data => {
              if (data.erro) {
                  throw new Error('CEP não encontrado');
              }

              // Format CEP with hyphen
              const formattedCEP = `${cleanCEP.slice(0, 5)}-${cleanCEP.slice(5)}`;

              // Get address data from Brasil API
              const addressData = {
                  cep: formattedCEP,
                  city: data.city,
                  state: data.state,
                  neighborhood: data.neighborhood,
                  street: data.street
              };

              addressData.updatedAt = Date.now();
              const serializedAddress = JSON.stringify(addressData);
              try {
                  localStorage.setItem('unilar-address', serializedAddress);
              } catch (error) {
                  console.warn('Erro ao salvar CEP no localStorage:', error);
              }
              try {
                  sessionStorage.setItem('unilar-address', serializedAddress);
              } catch (error) {
                  console.warn('Erro ao salvar CEP no sessionStorage:', error);
              }


              // Update desktop CEP display: "Entregar em Cidade, UF - CEP"
              desktopAddressTexts.forEach(container => {
                  // Verifica se já existe um label, se não, cria
                  let label = container.querySelector('.unilar-address-label');
                  if (!label) {
                      label = document.createElement('span');
                      label.className = 'unilar-address-label';
                      label.textContent = this.str.deliver_prefix;
                      container.insertBefore(label, container.firstChild);
                  }

                  // Atualiza o valor
                  const valueEl = container.querySelector('.unilar-address-value');
                  if (valueEl) {
                      valueEl.textContent = `${addressData.city}, ${addressData.state} - ${formattedCEP}`;
                  }
              });

              // Update mobile CEP display
              if (mobileCepTextContainer) {
                  // Verifica se já existe um label, se não, cria
                  let label = mobileCepTextContainer.querySelector('.unilar-address-label');
                  if (!label) {
                      label = document.createElement('span');
                      label.className = 'unilar-address-label';
                      label.textContent = this.str.deliver_prefix;
                      mobileCepTextContainer.insertBefore(label, mobileCepTextContainer.firstChild);
                  }

                  // Atualiza ou cria o elemento de valor
                  let mobileCepValue = mobileCepTextContainer.querySelector('.unilar-cep-default');
                  if (!mobileCepValue) {
                      mobileCepValue = document.createElement('span');
                      mobileCepValue.className = 'unilar-cep-default';
                      mobileCepTextContainer.appendChild(mobileCepValue);
                  }
                  mobileCepValue.textContent = `${addressData.city}, ${addressData.state} - ${formattedCEP}`;
              }

              // Trigger custom event for other components (e.g., shipping calculator)
              document.dispatchEvent(new CustomEvent('cep:updated', {
                  detail: addressData
              }));
          })
          .catch(error => {
              console.error('Erro ao buscar CEP:', error);

              // Restore default text
              desktopAddressTexts.forEach(container => {
                  // Remove o label se existir
                  const label = container.querySelector('.unilar-address-label');
                  if (label) {
                      label.remove();
                  }

                  const valueEl = container.querySelector('.unilar-address-value');
                  if (valueEl) {
                      valueEl.textContent = this.str.enter_cep;
                  }
              });

              if (mobileCepTextContainer) {
                  // Remove o label se existir
                  const label = mobileCepTextContainer.querySelector('.unilar-address-label');
                  if (label) {
                      label.remove();
                  }

                  const mobileCepValue = mobileCepTextContainer.querySelector('.unilar-cep-default');
                  if (mobileCepValue) {
                      mobileCepValue.textContent = this.str.enter_cep;
                  }
              }

              alert(this.str.cep_not_found_alert);
          });
  }

  useGeolocation(modal) {
      if (!navigator.geolocation) {
          alert(this.str.geo_unavailable);
          return;
      }

      // Show loading state
      const useLocationBtn = document.querySelector('[data-use-location]');
      if (useLocationBtn) {
          useLocationBtn.disabled = true;
          const originalText = useLocationBtn.innerHTML;
          useLocationBtn.innerHTML = '<span>' + escapeHtml(this.str.geo_fetching) + '</span>';

          navigator.geolocation.getCurrentPosition(
              (position) => {
                  const { latitude, longitude } = position.coords;

                  // Use Nominatim API for reverse geocoding (free and no API key required)
                  fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`, {
                      headers: {
                          'User-Agent': 'unilarStore/1.0'
                      }
                  })
                      .then(response => response.json())
                      .then(data => {
                          // Extract postcode from Nominatim response
                          const postcode = data.address?.postcode;

                          if (postcode) {
                              // Clean and validate CEP
                              const cleanCEP = postcode.replace(/\D/g, '');

                              if (cleanCEP.length === 8) {
                                  this.updateCEP(cleanCEP);
                                  this.closeModal(modal);
                              } else {
                                  throw new Error('CEP inválido obtido da localização');
                              }
                          } else {
                              throw new Error('CEP não encontrado para esta localização');
                          }
                      })
                      .catch(error => {
                          console.error('Erro no reverse geocoding:', error);
                          alert(this.str.geo_reverse_failed);
                      })
                      .finally(() => {
                          useLocationBtn.disabled = false;
                          useLocationBtn.innerHTML = originalText;
                      });
              },
              (error) => {
                  console.error('Erro ao obter localização:', error);

                  let errorMessage = this.str.geo_generic_error;

                  switch (error.code) {
                      case error.PERMISSION_DENIED:
                          errorMessage = this.str.geo_permission_denied;
                          break;
                      case error.POSITION_UNAVAILABLE:
                          errorMessage = this.str.geo_position_unavailable;
                          break;
                      case error.TIMEOUT:
                          errorMessage = this.str.geo_timeout;
                          break;
                  }

                  alert(errorMessage);

                  useLocationBtn.disabled = false;
                  useLocationBtn.innerHTML = originalText;
              }
          );
      }
  }

  // ========== Account Dropdown ==========
  initAccountDropdown() {
      const accountBtn = document.querySelector('[data-account-trigger]');
      const accountDropdown = document.querySelector('[data-account-dropdown]');

      if (!accountBtn || !accountDropdown) return;

      accountBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          accountDropdown.classList.toggle('unilar-hidden');
      });

      // Close dropdown when clicking outside
      document.addEventListener('click', (e) => {
          if (!accountBtn.contains(e.target) && !accountDropdown.contains(e.target)) {
              accountDropdown.classList.add('unilar-hidden');
          }
      });
  }

  // ========== Megamenu ==========
  initMegamenu() {
      const navLinks = document.querySelectorAll('[data-megamenu-trigger]');
      const megamenus = document.querySelectorAll('[data-megamenu]');

      if (!navLinks.length) return;

      let currentMegamenu = null;
      let closeTimeout = null;

      navLinks.forEach(link => {
          const targetId = link.dataset.megamenuTrigger;
          const megamenu = document.querySelector(`[data-megamenu="${targetId}"]`);

          if (!megamenu) return;

          // Show megamenu on hover
          link.addEventListener('mouseenter', () => {
              if (closeTimeout) {
                  clearTimeout(closeTimeout);
                  closeTimeout = null;
              }

              // Hide all other megamenus
              megamenus.forEach(m => {
                  if (m !== megamenu) {
                      m.classList.add('unilar-hidden');
                  }
              });

              // Remove active state from all nav links
              navLinks.forEach(l => l.classList.remove('active'));

              // Show current megamenu
              megamenu.classList.remove('unilar-hidden');
              link.classList.add('active');
              currentMegamenu = megamenu;
          });

          // Keep megamenu open when hovering over it
          megamenu.addEventListener('mouseenter', () => {
              if (closeTimeout) {
                  clearTimeout(closeTimeout);
                  closeTimeout = null;
              }
          });

          // Close megamenu when mouse leaves
          const handleMouseLeave = () => {
              closeTimeout = setTimeout(() => {
                  megamenu.classList.add('unilar-hidden');
                  link.classList.remove('active');
                  currentMegamenu = null;
              }, 200);
          };

          link.addEventListener('mouseleave', handleMouseLeave);
          megamenu.addEventListener('mouseleave', handleMouseLeave);
      });

      // Handle megamenu tabs (subcategories)
      const megamenuTabs = document.querySelectorAll('[data-megamenu-tab]');
      megamenuTabs.forEach(tab => {
          tab.addEventListener('click', () => {
              const targetId = tab.dataset.megamenuTab;
              const parentMegamenu = tab.closest('[data-megamenu]');
              const tabContents = parentMegamenu.querySelectorAll('[data-megamenu-content]');

              // Remove active state from all tabs
              parentMegamenu.querySelectorAll('[data-megamenu-tab]').forEach(t => {
                  t.classList.remove('active');
              });

              // Hide all tab contents
              tabContents.forEach(content => {
                  content.classList.add('unilar-hidden');
              });

              // Show active tab content
              const activeContent = parentMegamenu.querySelector(`[data-megamenu-content="${targetId}"]`);
              if (activeContent) {
                  activeContent.classList.remove('unilar-hidden');
              }

              tab.classList.add('active');
          });
      });
  }

  // ========== Mobile Menu ==========
  initMobileMenu() {
      const menuTrigger = document.querySelector('[data-mobile-menu-trigger]');
      const mobileMenu = document.querySelector('[data-mobile-menu]');
      const menuClose = document.querySelector('[data-mobile-menu-close]');

      if (!menuTrigger || !mobileMenu) return;

      menuTrigger.addEventListener('click', () => {
          this.openMobileMenu(mobileMenu);
      });

      if (menuClose) {
          menuClose.addEventListener('click', () => {
              this.closeMobileMenu(mobileMenu);
          });
      }

      // Handle mobile nav item with children
      const navItemsWithChildren = document.querySelectorAll('[data-mobile-nav-parent]');
      navItemsWithChildren.forEach(item => {
          item.addEventListener('click', (e) => {
              e.preventDefault();
              const submenu = item.nextElementSibling;
              if (submenu && submenu.hasAttribute('data-mobile-submenu')) {
                  submenu.classList.toggle('unilar-hidden');
              }
          });
      });
  }

  openMobileMenu(menu) {
      if (!menu) return;
      menu.classList.remove('unilar-hidden');
      document.body.style.overflow = 'hidden';
  }

  closeMobileMenu(menu) {
      if (!menu) return;
      menu.classList.add('unilar-hidden');
      document.body.style.overflow = '';
  }

  // ========== Cart Drawer ==========
  initMinicart() {
      document.addEventListener('cart:update', this.#onCartUpdate);

      const triggers = document.querySelectorAll('[data-minicart-trigger]');
      if (!triggers.length) return;

      const openDrawer = async () => {
          await customElements.whenDefined('cart-drawer-component');
          const drawer = document.querySelector('cart-drawer-component');
          if (drawer && typeof drawer.open === 'function') {
              drawer.open();
          }
      };

      triggers.forEach((trigger) => {
          trigger.addEventListener('click', (event) => {
              event.preventDefault();
              openDrawer();
          });
      });
  }

  #onCartUpdate = (event) => {
      const itemCount = event?.detail?.resource?.item_count;
      if (typeof itemCount === 'number') {
          this.updateCartCount(itemCount);
          return;
      }

      const addedCount = event?.detail?.data?.itemCount;
      if (typeof addedCount !== 'number') return;

      // Fallback when the event only includes the added quantity, not the cart total.
      fetch('/cart.js')
          .then((response) => response.json())
          .then((cart) => {
              if (typeof cart?.item_count === 'number') {
                  this.updateCartCount(cart.item_count);
              }
          })
          .catch(() => {});
  };

  updateCartCount(itemCount) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => {
          if (el.classList.contains('cart-heading__count') || el.classList.contains('cart-drawer__heading-count')) {
              el.textContent = `(${itemCount})`;
              return;
          }

          el.textContent = itemCount > 0 ? String(itemCount) : '';
      });

      document.querySelectorAll('.unilar-mobile-cart-btn').forEach((button) => {
          let countEl = button.querySelector('.unilar-mobile-cart-count');

          if (itemCount > 0) {
              if (!countEl) {
                  countEl = document.createElement('span');
                  countEl.className = 'unilar-mobile-cart-count';
                  button.appendChild(countEl);
              }
              countEl.textContent = String(itemCount);
              countEl.style.display = '';
          } else if (countEl) {
              countEl.remove();
          }
      });
  }

  // ========== Countdown Timer ==========
  initCountdown() {
      const countdownElement = document.querySelector('[data-countdown]');
      if (!countdownElement) return;

      const endDateStr = countdownElement.dataset.countdown;
      if (!endDateStr) return;

      const endDate = new Date(endDateStr).getTime();

      const updateCountdown = () => {
          const now = new Date().getTime();
          const distance = endDate - now;

          if (distance < 0) {
              countdownElement.style.display = 'none';
              return;
          }

          const days = Math.floor(distance / (1000 * 60 * 60 * 24));
          const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
          const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((distance % (1000 * 60)) / 1000);

          const daysEl = countdownElement.querySelector('[data-countdown-days]');
          const hoursEl = countdownElement.querySelector('[data-countdown-hours]');
          const minutesEl = countdownElement.querySelector('[data-countdown-minutes]');
          const secondsEl = countdownElement.querySelector('[data-countdown-seconds]');

          if (daysEl) daysEl.textContent = String(days).padStart(2, '0');
          if (hoursEl) hoursEl.textContent = String(hours).padStart(2, '0');
          if (minutesEl) minutesEl.textContent = String(minutes).padStart(2, '0');
          if (secondsEl) secondsEl.textContent = String(seconds).padStart(2, '0');
      };

      // Update immediately
      updateCountdown();

      // Update every second
      setInterval(updateCountdown, 1000);
  }

  // ========== Helper Methods ==========
  openModal(modal) {
      if (!modal) return;
      modal.classList.remove('unilar-hidden');
      document.body.style.overflow = 'hidden';
  }

  closeModal(modal) {
      if (!modal) return;
      modal.classList.add('unilar-hidden');
      document.body.style.overflow = '';
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
      new unilarHeader();
  });
} else {
  new unilarHeader();
}

// Export for use in other scripts if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = unilarHeader;
}

// Custom Element para Dropdown Menu
class DropdownMenu extends HTMLElement {
  constructor() {
      super();
      this.currentOpenDropdown = null;
  }

  connectedCallback() {
      this.setupEventListeners();
  }

  setupEventListeners() {
      // Adicionar eventos de clique para todos os dropdown toggles
      const dropdownToggles = this.querySelectorAll('[data-dropdown-toggle]');

      dropdownToggles.forEach(toggle => {
          toggle.addEventListener('click', (e) => this.handleToggleClick(e, toggle));
      });

      this.setupDepartmentsSubmenus();

      // Fechar dropdowns ao clicar fora
      document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) {
              this.closeAllDropdowns();
          }
      });
  }

  setupDepartmentsSubmenus() {
      const departmentsItems = this.querySelectorAll('.menu-item-departments');

      departmentsItems.forEach(departmentsItem => {
          const toggles = departmentsItem.querySelectorAll('[data-departments-submenu-toggle]');

          toggles.forEach(toggle => {
              toggle.addEventListener('click', event => {
                  if (!window.matchMedia('(min-width: 992px)').matches) return;

                  event.preventDefault();
                  event.stopPropagation();

                  const item = toggle.closest('.menu-dropdown > .menu-item');
                  const shouldOpen = !item?.classList.contains('is-open');

                  toggles.forEach(siblingToggle => {
                      siblingToggle.closest('.menu-dropdown > .menu-item')?.classList.remove('is-open');
                      siblingToggle.setAttribute('aria-expanded', 'false');
                  });

                  if (shouldOpen && item) {
                      item.classList.add('is-open');
                      toggle.setAttribute('aria-expanded', 'true');
                  }
              });
          });

          departmentsItem.addEventListener('mouseleave', () => {
              toggles.forEach(toggle => {
                  toggle.closest('.menu-dropdown > .menu-item')?.classList.remove('is-open');
                  toggle.setAttribute('aria-expanded', 'false');
              });
          });
      });
  }

  handleToggleClick(event, toggle) {
      // Se for desktop, permite a navegação pelo link
      if (window.matchMedia('(min-width: 992px)').matches) {
          return;
      }

      event.preventDefault();
      event.stopPropagation();

      const wrapper = toggle.nextElementSibling;
      const dropdown = wrapper?.querySelector('.dropdown-menu');

      if (!dropdown) return;

      const isCurrentlyOpen = dropdown.classList.contains('show');

      // Fechar dropdowns do mesmo nível
      this.closeSiblingDropdowns(toggle);

      // Toggle do dropdown atual
      if (isCurrentlyOpen) {
          this.closeDropdown(toggle, dropdown);
      } else {
          this.openDropdown(toggle, dropdown);
      }
  }

  closeSiblingDropdowns(currentToggle) {
      const parentLi = currentToggle.closest('li');
      const parentUl = parentLi.parentElement;
      const siblingItems = parentUl.querySelectorAll(':scope > li');

      siblingItems.forEach(item => {
          if (item !== parentLi) {
              const siblingToggle = item.querySelector(':scope > [data-dropdown-toggle]');
              if (siblingToggle) {
                  const siblingWrapper = siblingToggle.nextElementSibling;
                  const siblingDropdown = siblingWrapper?.querySelector('.dropdown-menu');
                  if (siblingDropdown?.classList.contains('show')) {
                      this.closeDropdown(siblingToggle, siblingDropdown);
                  }
              }
          }
      });
  }

  openDropdown(toggle, dropdown) {
      const wrapper = toggle.nextElementSibling;

      dropdown.classList.add('show');
      wrapper?.classList.add('show');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.classList.add('show');
      this.currentOpenDropdown = dropdown;

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('dropdown-opened', {
          detail: { toggle, dropdown, wrapper },
          bubbles: true
      }));
  }

  closeDropdown(toggle, dropdown) {
      const wrapper = toggle.nextElementSibling;

      dropdown.classList.remove('show');
      wrapper?.classList.remove('show');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.classList.remove('show');

      // Fechar todos os sub-dropdowns dentro deste dropdown
      const subDropdowns = dropdown.querySelectorAll('.dropdown-menu.show');
      const subWrappers = dropdown.querySelectorAll('.wrapper.show');

      subDropdowns.forEach(subDropdown => {
          subDropdown.classList.remove('show');
          const subWrapper = subDropdown.parentElement;
          subWrapper?.classList.remove('show');
          const subToggle = subWrapper?.previousElementSibling;
          if (subToggle) {
              subToggle.setAttribute('aria-expanded', 'false');
              subToggle.classList.remove('show');
          }
      });

      if (this.currentOpenDropdown === dropdown) {
          this.currentOpenDropdown = null;
      }

      // Dispatch custom event
      this.dispatchEvent(new CustomEvent('dropdown-closed', {
          detail: { toggle, dropdown, wrapper },
          bubbles: true
      }));
  }

  closeAllDropdowns() {
      const allDropdowns = this.querySelectorAll('.dropdown-menu.show');
      allDropdowns.forEach(dropdown => {
          const toggle = dropdown.closest('.wrapper')?.previousElementSibling;
          if (toggle) {
              this.closeDropdown(toggle, dropdown);
          }
      });
  }
}

// Registrar o custom element
if (!customElements.get('dropdown-menu')) {
  customElements.define('dropdown-menu', DropdownMenu);
}

// Custom Element para Lateral Menu Dropdown
class LateralMenuDropdown extends HTMLElement {
  constructor() {
      super();
      this.currentOpenDropdown = null;
  }

  connectedCallback() {
      this.setupEventListeners();
  }

  setupEventListeners() {
      const toggles = this.querySelectorAll('[data-lateral-toggle]');

      toggles.forEach(toggle => {
          toggle.addEventListener('click', (e) => this.handleToggleClick(e, toggle));
      });

      // Fechar dropdown ao clicar fora
      document.addEventListener('click', (e) => {
          if (!this.contains(e.target)) {
              this.closeAllDropdowns();
          }
      });
  }

  handleToggleClick(event, toggle) {
      event.preventDefault();
      event.stopPropagation();

      const parentLi = toggle.closest('.nav-item-lateral');
      const dropdown = parentLi?.querySelector('.sub-nav-links');

      if (!dropdown) return;

      const isCurrentlyOpen = dropdown.classList.contains('show');

      // Fechar outros dropdowns
      this.closeAllDropdowns();

      // Toggle do dropdown atual
      if (!isCurrentlyOpen) {
          this.openDropdown(toggle, dropdown);
      }
  }

  openDropdown(toggle, dropdown) {
      dropdown.classList.add('show');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.classList.add('show');
      this.currentOpenDropdown = dropdown;
  }

  closeDropdown(toggle, dropdown) {
      dropdown.classList.remove('show');
      toggle?.setAttribute('aria-expanded', 'false');
      toggle?.classList.remove('show');

      if (this.currentOpenDropdown === dropdown) {
          this.currentOpenDropdown = null;
      }
  }

  closeAllDropdowns() {
      const allDropdowns = this.querySelectorAll('.sub-nav-links.show');
      allDropdowns.forEach(dropdown => {
          const parentLi = dropdown.closest('.nav-item-lateral');
          const toggle = parentLi?.querySelector('[data-lateral-toggle]');
          this.closeDropdown(toggle, dropdown);
      });
  }
}

// Registrar o custom element
if (!customElements.get('lateral-menu-dropdown')) {
  customElements.define('lateral-menu-dropdown', LateralMenuDropdown);
}

// ========== Mobile Menu Component ==========
class unilarMobileMenu extends HTMLElement {
  constructor() {
      super();
      this.details = this.querySelector('.unilar-mobile-menu-container');
      this.overlay = this.querySelector('.unilar-mobile-menu-overlay');
      this.closeButtons = this.querySelectorAll('.unilar-mobile-menu-close');
      this.portaledSubmenus = new Set();

      this.init();
  }

  init() {
      if (!this.details) return;

      // Close buttons (main header + submenus)
      this.closeButtons.forEach(button => {
          button.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.close();
          });
      });

      // Account button inside mobile menu
      const accountBtn = this.querySelector('.unilar-mobile-account-btn');
      if (accountBtn) {
          accountBtn.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              // Open account modal without closing menu
              document.dispatchEvent(new CustomEvent('unilar:open-account-modal'));
          });
      }

      // Submenu back buttons (delegated — panels may be portaled outside details)
      this.handleSubmenuBack = (e) => {
          const button = e.target.closest('.unilar-mobile-submenu-back');
          if (!button || !this.details.open) return;

          const panel = button.closest('.unilar-mobile-submenu, .unilar-mobile-sub-submenu');
          if (!panel) return;

          const submenuDetails = this.getSubmenuDetailsForPanel(panel);
          if (!submenuDetails) return;

          e.preventDefault();
          e.stopPropagation();
          submenuDetails.removeAttribute('open');

          if (panel.dataset.portaled === 'true') {
              this.unportalSubmenu(panel);
          }
      };

      document.addEventListener('click', this.handleSubmenuBack);

      // Handle details toggle
      this.details.addEventListener('toggle', (e) => {
          if (this.details.open) {
              this.open();
          } else {
              this.onClose();
          }
      });

      // Close on escape key
      document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.details.open) {
              this.close();
          }
      });

      // Close when clicking overlay
      if (this.overlay) {
          this.overlay.addEventListener('click', (e) => {
              e.preventDefault();
              e.stopPropagation();
              this.close();
          });
      }

      this.handleSubmenuToggle = (e) => {
          if (!this.details?.open) return;

          const submenuDetails = e.target;
          if (!submenuDetails?.matches?.('.unilar-mobile-submenu-details')) return;
          if (!this.isSubmenuDetailsManaged(submenuDetails)) return;

          if (submenuDetails.open) {
              const panel = submenuDetails.querySelector(':scope > .unilar-mobile-submenu, :scope > .unilar-mobile-sub-submenu');
              if (panel) this.portalSubmenu(submenuDetails, panel);
          } else {
              const panel = this.getPortaledPanelForDetails(submenuDetails);
              if (panel?.dataset.portaled === 'true') this.unportalSubmenu(panel);
          }
      };

      document.addEventListener('toggle', this.handleSubmenuToggle, true);
  }

  isSubmenuDetailsManaged(submenuDetails) {
      if (this.contains(submenuDetails)) return true;

      for (const panel of this.portaledSubmenus) {
          if (panel.contains(submenuDetails)) return true;
      }

      return false;
  }

  getSubmenuDetailsForPanel(panel) {
      if (!panel) return null;

      if (panel._submenuDetails?.classList?.contains('unilar-mobile-submenu-details')) {
          return panel._submenuDetails;
      }

      if (panel._submenuParent?.classList?.contains('unilar-mobile-submenu-details')) {
          return panel._submenuParent;
      }

      return panel.closest('.unilar-mobile-submenu-details');
  }

  getPortaledPanelForDetails(submenuDetails) {
      for (const panel of this.portaledSubmenus) {
          if (this.getSubmenuDetailsForPanel(panel) === submenuDetails) {
              return panel;
          }
      }

      return submenuDetails.querySelector(':scope > .unilar-mobile-submenu, :scope > .unilar-mobile-sub-submenu');
  }

  open() {
      this.updateDrawerPosition();
      this.updateSubmenuViewport();
      document.body.classList.add('unilar-mobile-menu-open');
      this.trapFocus();
  }

  getViewportHeight() {
      return Math.round(window.visualViewport?.height || window.innerHeight);
  }

  updateSubmenuViewport() {
      const viewportHeight = this.getViewportHeight();
      document.documentElement.style.setProperty('--mobile-submenu-height', `${viewportHeight}px`);

      this.portaledSubmenus.forEach((panel) => {
          panel.style.height = `${viewportHeight}px`;
          panel.style.minHeight = `${viewportHeight}px`;
      });
  }

  bindViewportListener() {
      if (this.viewportListener) return;

      this.viewportListener = () => this.updateSubmenuViewport();
      window.visualViewport?.addEventListener('resize', this.viewportListener);
      window.visualViewport?.addEventListener('scroll', this.viewportListener);
      window.addEventListener('resize', this.viewportListener);
  }

  unbindViewportListener() {
      if (!this.viewportListener) return;

      window.visualViewport?.removeEventListener('resize', this.viewportListener);
      window.visualViewport?.removeEventListener('scroll', this.viewportListener);
      window.removeEventListener('resize', this.viewportListener);
      this.viewportListener = null;
  }

  portalSubmenu(details, panel) {
      if (panel.dataset.portaled === 'true') return;

      const placeholder = document.createComment('unilar-submenu-placeholder');
      panel._submenuPlaceholder = placeholder;
      panel._submenuParent = panel.parentNode;
      panel.parentNode.insertBefore(placeholder, panel);

      document.body.appendChild(panel);
      panel.dataset.portaled = 'true';
      panel._submenuDetails = details;
      panel.classList.add('unilar-mobile-submenu--portaled');
      this.portaledSubmenus.add(panel);

      this.bindViewportListener();
      this.updateSubmenuViewport();

      panel.classList.remove('unilar-mobile-submenu--visible');
      requestAnimationFrame(() => {
          requestAnimationFrame(() => {
              panel.classList.add('unilar-mobile-submenu--visible');
          });
      });
  }

  unportalSubmenu(panel, immediate = false) {
      if (!panel || panel.dataset.portaled !== 'true' || panel.dataset.unportaling === 'true') return;

      panel.dataset.unportaling = 'true';
      let restored = false;
      const restore = () => {
          if (restored) return;
          restored = true;

          if (panel._submenuPlaceholder && panel._submenuParent?.isConnected) {
              panel._submenuParent.insertBefore(panel, panel._submenuPlaceholder);
              panel._submenuPlaceholder.remove();
          }

          panel.classList.remove('unilar-mobile-submenu--portaled', 'unilar-mobile-submenu--visible');
          delete panel.dataset.portaled;
          delete panel.dataset.unportaling;
          panel.style.removeProperty('height');
          panel.style.removeProperty('min-height');
          delete panel._submenuPlaceholder;
          delete panel._submenuParent;
          delete panel._submenuDetails;
          this.portaledSubmenus.delete(panel);

          if (this.portaledSubmenus.size === 0) {
              this.unbindViewportListener();
          }
      };

      if (immediate) {
          restore();
          return;
      }

      panel.classList.remove('unilar-mobile-submenu--visible');
      panel.addEventListener('transitionend', restore, { once: true });
      setTimeout(restore, 350);
  }

  restoreAllPortaledSubmenus() {
      Array.from(this.portaledSubmenus).forEach((panel) => {
          this.unportalSubmenu(panel, true);
      });
  }

  updateDrawerPosition() {
      const drawer = this.querySelector('.unilar-mobile-menu-drawer');
      const mobileTop = document.querySelector('.unilar-mobile-top');
      if (!drawer || !mobileTop) return;

      const topOffset = mobileTop.getBoundingClientRect().bottom;
      drawer.style.setProperty('--mobile-menu-inset-top', `${Math.round(topOffset)}px`);
  }

  close() {
      this.details.removeAttribute('open');
  }

  onClose() {
      document.body.classList.remove('unilar-mobile-menu-open');
      this.removeFocusTrap();
      this.restoreAllPortaledSubmenus();
      this.unbindViewportListener();
      document.documentElement.style.removeProperty('--mobile-submenu-height');

      // Close all open submenus
      this.querySelectorAll('details[open]').forEach(detail => {
          if (detail !== this.details) {
              detail.removeAttribute('open');
          }
      });
  }

  trapFocus() {
      const focusableElements = this.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      this.handleTabKey = (e) => {
          if (e.key !== 'Tab') return;

          if (e.shiftKey) {
              if (document.activeElement === firstElement) {
                  e.preventDefault();
                  lastElement.focus();
              }
          } else {
              if (document.activeElement === lastElement) {
                  e.preventDefault();
                  firstElement.focus();
              }
          }
      };

      document.addEventListener('keydown', this.handleTabKey);
  }

  removeFocusTrap() {
      if (this.handleTabKey) {
          document.removeEventListener('keydown', this.handleTabKey);
      }
  }
}

// Register the custom element
if (!customElements.get('unilar-mobile-menu')) {
  customElements.define('unilar-mobile-menu', unilarMobileMenu);
}
